import { inject, Injectable } from '@angular/core';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { EMPTY, from, mergeMap, Observable, Subscription, take } from 'rxjs';
import { AuthService } from '../../services/auth/auth.service';
import { SignalRService } from '../../signalR/signal-r.service';
import { OAuthStorage } from 'angular-oauth2-oidc';
import { checkUrlForProxy } from '../../functions/check-url-for-proxy';
import { PwaService } from 'src/app/core/services/pwa/pwa.service';
import { NetworkService } from '../../services/network/network.service';
import { Dialog } from '@angular/cdk/dialog';
import { Router } from '@angular/router';
import { isPastDate } from 'src/app/shared/functions/helpers/is-past-date';
import { PwaReauthenticationRequiredModalComponent } from 'src/app/shared/components/modals/pwa-reauthentication-required-modal/pwa-reauthentication-required-modal.component';


@Injectable()
export class AuthInterceptor implements HttpInterceptor {
	// inject services using inject() function instead of constructor DI
	private authService = inject(AuthService);
	private signalrService = inject(SignalRService);
	private oAuthStorage = inject(OAuthStorage);
	private pwaService = inject(PwaService);
	private networkService = inject(NetworkService);
  private dialog = inject(Dialog);
  private router = inject(Router);
  private dialogRef: Subscription | undefined;

	public intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
		if (this.authService.isLoggedIn || (this.pwaService.isPwa() && !this.networkService.isOnline)) {
			if (checkUrlForProxy(request.url) && this.authService.rawAccessToken) {
        // Handle PWA specific logic
				if (this.pwaService.isPwa()) {
          return this.handlePwaRequests(request, next);
				}

				if (this.authService.tokenExpired) {
					return this.refreshTokenViaIdentityServer(request, next);
				} else {
					return next.handle(this.setAuthorizationHeader(request));
				}
			}
		} else {
			return EMPTY;
		}

		return next.handle(this.setReportAuthorizationHeader(request));
	}

  private refreshTokenViaIdentityServer(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return from(this.authService.refreshTokenViaIdentityServer()).pipe(
						take(1),
						mergeMap(() => {
							return next.handle(this.setAuthorizationHeader(request));
						})
					);
  }

	private handlePwaRequests(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const maxDateOfflinePwaUsage = localStorage.getItem('maxDateOfflinePwaUsage');
    const maxDateOfflinePwaUsageAsDate = maxDateOfflinePwaUsage ? new Date(maxDateOfflinePwaUsage) : Date.now();
		
		// 1. Check if case < 30 days offline -> Online (no modal, no re-login)
		// 2. Check if case > 30 days offline -> Online -> Modal + re-login
		// 3. Check if case 90 days offline -> Logout user -> cant use PWA anymore
        if (this.networkService.isOnline) {
          //ONLINE
          if (this.authService.tokenExpired && this.pwaService.isOnline) {
              return this.refreshTokenViaIdentityServer(request, next);
          } else {
              return next.handle(this.setAuthorizationHeader(request));
          }
				} else {
          //OFFLINE
          if(isPastDate(maxDateOfflinePwaUsageAsDate)){
            this.dialog.open(PwaReauthenticationRequiredModalComponent, {
  backdropClass: this.networkService.isOnline ? undefined : 'bizz-modal-backdrop-blur'
}).closed.pipe(take(1)).subscribe({
            next: (reAuthenticate) => {
              if (reAuthenticate) {
                const offlineItems = localStorage.getItem("offlineItems");
                localStorage.clear();
                localStorage.setItem("offlineItems", offlineItems ?? "");
                this.authService.initLogin(true);
              }
            }});
            return EMPTY;
          }else{
            // continue using app offline
            return next.handle(this.setAuthorizationHeader(request));
          }
				}
	}

  private setReportAuthorizationHeader(request: HttpRequest<unknown>): HttpRequest<unknown> {
    return request.clone({
      headers: request.headers.append('Authorization', 'Bearer ' + this.oAuthStorage.getItem('report_access_token'))
    });
  }

  private setAuthorizationHeader(request: HttpRequest<unknown>): HttpRequest<unknown> {
    if (this.signalrService.connectionId) {
      request = request.clone({ headers: request.headers.append('SignalRConnectionId', this.signalrService.connectionId) });
    }

    if (this.signalrService.aiConnectionId) {
      request = request.clone({ headers: request.headers.append('SignalRAiConnectionId', this.signalrService.aiConnectionId) });
    }

    if (this.signalrService.idpConnectionId) {
      request = request.clone({ headers: request.headers.append('SignalRIdpConnectionId', this.signalrService.idpConnectionId) });
    }

    if (this.authService.rawAccessToken) {
      return request.clone({
        headers: request.headers.append('Authorization', 'Bearer ' + this.authService.rawAccessToken)
      });
    } else {
      console.warn('Tried to set Authorization header but access token was null');
      return request;
    }
  }
}
